<script setup lang="ts">
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@/assets/multiselect.css'
import Multiselect from 'vue-multiselect'
import { defineProps, defineModel, computed } from 'vue'

const props = defineProps({
  options: {
    type: Array<{ id: string | number; text: string; subText?: string }>,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
})

const modelValue = defineModel<object | number | number[] | null>()

const selectedOptions = computed(() => {
  if (!modelValue.value) return []

  const ids = Array.isArray(modelValue.value) ? modelValue.value : [modelValue.value]

  return props.options.filter((option) => ids.includes(option.id as number))
})

const optionsValue = computed(() =>
  props.options.map((o) => ({
    id: o.id,
    text: o.text,
    subText: o.subText || '-',
  })),
)

function textLabel(option: { text: string; subText?: string }) {
  return option.subText ? `${option.text} (${option.subText})` : option.text
}

function handleModelUpdate(val: any) {
  if (props.multiple) {
    modelValue.value = val ? val.map((item: any) => item.id) : []
  } else {
    modelValue.value = val ? val.id : null
  }
}
</script>

<template>
  <label class="form-control w-full" :for="label">
    <div class="label">
      <span class="label-text font-semibold">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </span>
    </div>

    <Multiselect
      :model-value="selectedOptions"
      @update:model-value="handleModelUpdate"
      :options="optionsValue"
      :multiple="multiple"
      :required="required"
      :close-on-select="true"
      :searchable="true"
      :placeholder="'Sélectionner ' + label + '...'"
      select-label="Sélectionner"
      selected-label="Sélectionné"
      deselect-label="Retirer"
      label="text"
      track-by="id"
      :custom-label="textLabel"
    >
      <template #option="{ option }">
        <div class="w-full">
          <p class="font-bold">{{ option.text }}</p>
          <p class="text-sm">{{ option.subText }}</p>
        </div>
      </template>

      <template #noOptions>
        <div class="p-2">Aucun résultat trouvé</div>
      </template>
      <template #noResult>
        <div class="p-2">Aucune option disponible</div>
      </template>
    </Multiselect>
  </label>
</template>
